import type { AzureComponents } from "@p0security/integrations-azure-shared/components";
import { azureIamWriteComponentSetupFor } from "@p0security/integrations-azure-shared/helper";
import type { AzureIntegration } from "@p0security/integrations-azure-shared/types";
import {
  MICROSOFT_GRAPH_API_ID,
  toPermissionId,
} from "@p0security/integrations-entra-id-shared";
import type {
  FrontendInstallContext,
  FrontendInstaller,
} from "@p0security/integrations-installer";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import { installedItems } from "@p0security/shared/install/installed";
import type { Instructions } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

import { appRegistrationPreqMessages } from "./util";

// Grants User.Read.All to the app registration, needed by Azure IAM
// management to look up the requesting user in Entra when normalizing
// access requests.
const userReadPermissionSteps = (
  azureClientId?: string
): { header: string; command: string }[] => [
  {
    header:
      "Add the Microsoft Graph User.Read.All permission to the caller app registration to query Entra Users. This permission allows the app to read user profiles but not modify anything.",
    command: azureClientId
      ? `az ad app permission add --id ${azureClientId} --api ${MICROSOFT_GRAPH_API_ID} --api-permissions "${toPermissionId("User.Read.All")}=Role"`
      : "Unable to find client ID from P0 Azure App Registration. Please re-run the Azure App installation.",
  },
  {
    header:
      "Grant admin consent for the User.Read.All permission via the Microsoft Graph API. The command gets the service principal object ID for the P0 app registration and the Microsoft Graph service principal ID in this tenant, then grants admin consent for User.Read.All to the app registration's service principal.",
    command: azureClientId
      ? `APP_SERVICE_PRINCIPAL_ID=$(az ad sp create --id ${azureClientId} --query id --output tsv 2>/dev/null || az ad sp show --id ${azureClientId} --query id --output tsv)

GRAPH_SP_ID=$(az ad sp show --id ${MICROSOFT_GRAPH_API_ID} --query id --output tsv)

az rest --method POST \\
  --url "https://graph.microsoft.com/v1.0/servicePrincipals/$GRAPH_SP_ID/appRoleAssignedTo" \\
  --body "{
    \\"principalId\\": \\"$APP_SERVICE_PRINCIPAL_ID\\",
    \\"resourceId\\": \\"$GRAPH_SP_ID\\",
    \\"appRoleId\\": \\"${toPermissionId("User.Read.All")}\\"
  }"`
      : "Unable to find client ID from P0 Azure App Registration. Please re-run the Azure App installation.",
  },
];

// Terraform equivalent of userReadPermissionSteps
const userReadPermissionIacSteps = (
  azureClientId: string,
  clientServicePrincipalId: string
): { header: string; command: string }[] => [
  {
    header:
      "Add the User.Read.All Microsoft Graph permission to the caller app registration and grant admin consent. This permission allows the app to read user profiles but not modify anything.",
    command: `data "azuread_application" "p0_iam_write_app" {
  client_id = "${azureClientId}"
}

data "azuread_service_principal" "p0_iam_write_msgraph" {
  client_id = "${MICROSOFT_GRAPH_API_ID}"
}

# User.Read.All - read-only access to user profiles
resource "azuread_application_api_access" "p0_iam_write_msgraph" {
  application_id = data.azuread_application.p0_iam_write_app.id
  api_client_id  = "${MICROSOFT_GRAPH_API_ID}"
  role_ids       = ["${toPermissionId("User.Read.All")}"]
}

# Grant admin consent for User.Read.All
resource "azuread_app_role_assignment" "p0_iam_write_user_read" {
  app_role_id         = "${toPermissionId("User.Read.All")}"
  principal_object_id = "${clientServicePrincipalId}"
  resource_object_id  = data.azuread_service_principal.p0_iam_write_msgraph.object_id
}`,
  },
];

const azureIamWriteInstructionsFor = (
  context: FrontendInstallContext<AzureIntegration>,
  id: string
): Instructions<ReactNode> => {
  const azureApp = installedItems("azure-app", context.config);
  const { clientId, clientServicePrincipalId } = azureApp[SINGLETON_ITEM] ?? {};
  if (!clientId || !clientServicePrincipalId) {
    throw new Error("Azure App Registration is not installed.");
  }
  const { customRole } = azureIamWriteComponentSetupFor(
    id,
    clientServicePrincipalId
  );
  const {
    name: roleName,
    description,
    isCustom,
    actions,
    assignableScope,
    condition,
  } = customRole;
  const transformedActions = JSON.stringify(actions, null, 2);
  const shell = [
    ...userReadPermissionSteps(clientId),
    {
      header: "Target the subscription P0 is being installed on",
      command: `az account set --subscription ${id}`,
    },
    {
      header:
        "Create a P0 Management Role. The command creates a JSON file for the role definition, then creates the custom role from it.",

      command: `
    ${"" /** TODO: Check if the role already exists */}
  cat > p0-management-role.json << 'EOF'
{
  "Name": "${roleName}",
  "IsCustom": ${isCustom},
  "Description": "${description}",
  "Actions": ${transformedActions},
  "NotActions": [],
  "AssignableScopes": [
        "${assignableScope}"
  ]
  ${"" /*TODO ENG-4935: Prevent self-modification of role assignments */}
}
EOF

az role definition create --role-definition p0-management-role.json`,
    },
    {
      header:
        "Create role assignment of service principal to new P0 Management Role in the current subscription",
      command: `az role assignment create --assignee ${clientId} --role "${roleName}" --scope ${assignableScope} --condition "${condition}"`,
    },
  ];
  const iac = [
    ...userReadPermissionIacSteps(clientId, clientServicePrincipalId),
    {
      header: "Create a P0 Management Role",
      command: `resource "azurerm_role_definition" "p0_azure_custom_role" {
  name       = "${roleName}"
  scope       = "${assignableScope}"
  description = "${description}"

  permissions {
    actions     = ${transformedActions}
    not_actions = []
  }

  assignable_scopes = [
    "${assignableScope}"
  ]
}
    `,
    },
    {
      header:
        "Create role assignment of service principal to new P0 Management Role in the current subscription",
      command: `
resource "azurerm_role_assignment" "p0_role_assignment" {
  depends_on         = [azurerm_role_definition.p0_azure_custom_role]
  scope              = "${assignableScope}"
  role_definition_id = azurerm_role_definition.p0_azure_custom_role.role_definition_resource_id
  principal_type     = "ServicePrincipal"

  principal_id = "${clientServicePrincipalId}"
  condition    = <<-EOT
  ${condition}
  EOT
}`,
    },
  ];
  return {
    help: (
      <Typography.Paragraph>
        In order to provision access to your Azure subscription, we need to
        create a custom role and assign it to the Azure App Registration
        you&apos;ve already integrated with P0.
      </Typography.Paragraph>
    ),
    commands: {
      shell,
      iac,
    },
  };
};

export const azureIamWriteInstaller: FrontendInstaller<
  typeof AzureComponents
>["components"]["iam-write"] = {
  prerequisiteMessages: appRegistrationPreqMessages,
  instructions: azureIamWriteInstructionsFor,
  items: {},
};
